import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import accountingApi from '../../services/accountingApi';

function TrialBalance() {
    const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split('T')[0]);
    const [accountGroups, setAccountGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        loadTrialBalance();
    }, [asOfDate]);

    const loadTrialBalance = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await accountingApi.getTrialBalance({
                as_of_date: asOfDate
            });

            if (response.success) {
                setAccountGroups(response.data || []);
            } else {
                setError(response.message || 'Failed to load trial balance');
            }
        } catch (err) {
            console.error('Error loading trial balance:', err);
            setError('An error occurred while loading the trial balance');
        } finally {
            setLoading(false);
        }
    };

    const totalDebit = accountGroups.reduce((sum, group) => sum + (parseFloat(group.debit) || 0), 0);
    const totalCredit = accountGroups.reduce((sum, group) => sum + (parseFloat(group.credit) || 0), 0);
    const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;

    return (
        <div className="max-w-7xl mx-auto px-4 py-8">
            <div className="mb-6">
                <div className="flex items-center gap-3 mb-2">
                    <Link to="/accounting" className="p-2 text-slate-400 hover:text-indigo-600 rounded-lg transition-colors">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                    </Link>
                    <h1 className="text-2xl font-bold text-slate-800">Trial Balance</h1>
                </div>
                <p className="text-sm text-slate-600">Summary of all account balances</p>
            </div>

            {/* Filter */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-6">
                <div className="max-w-xs">
                    <label className="block text-sm font-bold text-slate-700 mb-2">As of Date</label>
                    <input
                        type="date"
                        value={asOfDate}
                        onChange={(e) => setAsOfDate(e.target.value)}
                        className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-4 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all"
                    />
                </div>
            </div>

            {loading && (
                <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                    <p className="mt-2 text-slate-600">Loading trial balance...</p>
                </div>
            )}

            {error && (
                <div className="bg-rose-50 border-l-4 border-rose-500 p-4 rounded-md mb-6">
                    <p className="text-sm text-rose-700">{error}</p>
                </div>
            )}

            {!isBalanced && !loading && accountGroups.length > 0 && (
                <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-md mb-6">
                    <p className="text-sm text-amber-700 font-bold">⚠️ Trial Balance is not balanced! Difference: ₹{Math.abs(totalDebit - totalCredit).toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                </div>
            )}

            {!loading && (
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase">Account Group</th>
                                    <th className="px-6 py-3 text-left text-xs font-bold text-slate-500 uppercase">Account Head</th>
                                    <th className="px-6 py-3 text-right text-xs font-bold text-slate-500 uppercase">Debit (₹)</th>
                                    <th className="px-6 py-3 text-right text-xs font-bold text-slate-500 uppercase">Credit (₹)</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {accountGroups.length > 0 ? (
                                    accountGroups.map((group) => (
                                        <tr key={group.id} className="hover:bg-slate-50">
                                            <td className="px-6 py-4 text-sm font-medium text-slate-900">{group.name}</td>
                                            <td className="px-6 py-4 text-sm text-slate-600">{group.account_head}</td>
                                            <td className="px-6 py-4 text-sm text-right font-medium text-slate-900">
                                                {parseFloat(group.debit) > 0 ? `₹${parseFloat(group.debit).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '-'}
                                            </td>
                                            <td className="px-6 py-4 text-sm text-right font-medium text-slate-900">
                                                {parseFloat(group.credit) > 0 ? `₹${parseFloat(group.credit).toLocaleString(undefined, { minimumFractionDigits: 2 })}` : '-'}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="4" className="px-6 py-12 text-center text-slate-400 italic">No account groups found</td>
                                    </tr>
                                )}
                                {accountGroups.length > 0 && (
                                    <tr className={`border-t-2 ${isBalanced ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`}>
                                        <td colSpan="2" className={`px-6 py-4 text-sm font-bold ${isBalanced ? 'text-emerald-900' : 'text-rose-900'}`}>
                                            Total {isBalanced && '✓'}
                                        </td>
                                        <td className={`px-6 py-4 text-sm text-right font-bold ${isBalanced ? 'text-emerald-900' : 'text-rose-900'}`}>
                                            ₹{totalDebit.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                        </td>
                                        <td className={`px-6 py-4 text-sm text-right font-bold ${isBalanced ? 'text-emerald-900' : 'text-rose-900'}`}>
                                            ₹{totalCredit.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

export default TrialBalance;
